import axios from 'axios';
import { notice, clearLogin } from "@/config/default/funs";
import { API_URL, timeout } from "../../../public/config";
import router from "@/router";

// 创建Axios实例
const defaultConfig = {
  baseURL: API_URL || '',
  timeout: timeout || 600000, // 默认10分钟超时
};

const getLanguage = () => {
  const store = require("@/store").default;
  return store?.state?.language || 'cn';
};

const customAxios = axios.create(defaultConfig);

// 请求存储管理
const pendingRequests = new Map(); // 格式: [requestKey: AbortController]
const inFlightRequests = new Map(); // 请求去重存储

// 生成请求唯一标识
const getRequestKey = (config) => {
  // const { method, url, data, params } = config;
  // const timestampSec = Math.floor(Date.now() / 1000); // 1711277280
  // const lang = getLanguage();
  // return `${method}:${url}:${JSON.stringify(data || params)}:${lang}:${timestampSec}`;

  const { method, url } = config;
  const timestampSec = Math.floor(Date.now() / 1000); // 1711277280
  const lang = getLanguage();
  return `${method}:${url}:${lang}:${timestampSec}`;
};

// // 获取当前页面信息
// const getPageInfo = () => {
//   // Vue Router 方式
//   const currentRoute = router.currentRoute?.value;
  
//   // 或使用原生方式
//   const pageId = document.querySelector('meta[name="page-id"]')?.content;
//   const metaTimestamp = document.querySelector('meta[timestamp]')?.content;
  
//   return {
//     name: currentRoute?.name || pageId || 'unknown',
//     path: currentRoute?.path || window.location.pathname,
//     fullPath: currentRoute?.fullPath || window.location.href,
//     metaTime: metaTimestamp,
//     requestTime: new Date().toISOString()
//   };
// };

// 请求拦截器
customAxios.interceptors.request.use(
  config => {
    // 请求去重处理
    const requestKey = getRequestKey(config);
    // const page = getPageInfo()
    // console.log('请求---requestKey:', requestKey, "------pageInfo:", page.name);
    
    if (inFlightRequests.has(requestKey)) {
      const msg = getLanguage() === 'en' ? 'Repeat request: ' : '重复请求：';
      const message = `${msg}${requestKey}`;
      inFlightRequests.get(requestKey).abort(message);
      inFlightRequests.delete(requestKey);
      // return Promise.reject(new Error(message)); // 直接返回拒绝
    }

    // 创建AbortController
    const controller = new AbortController();
    config.signal = controller.signal;
    
    // 存储请求
    pendingRequests.set(requestKey, controller);
    inFlightRequests.set(requestKey, controller);
    
    return config;
  },
  error => Promise.reject(error)
);

// 响应拦截器
customAxios.interceptors.response.use(
  async response => {
    const { config } = response;
    const requestKey = getRequestKey(config);
    
    // 清理已完成请求
    pendingRequests.delete(requestKey);
    inFlightRequests.delete(requestKey);

    const lang = getLanguage();
    const result = response.data;
    if (typeof result === 'string') {
      try {
        return JSON.parse(result);
      } catch (e) {        
        const tips = lang === 'en' ? 'error' : '错误';
        const msg = lang === 'en' ? 'Data in wrong format' : '数据格式错误';
        notice(tips, msg , 'error');
        return Promise.reject(result);
      }
    }
    
    let tips, msg;
    // 状态码处理
    switch (result.status) {
      case 200:
        return result;
      case 401: // 未登录
        await handleUnauthorized(result);
        return Promise.reject(result);
      case 403:
      case 404:
      case 500:
        tips = lang === 'en' ? 'error' : '错误';
        msg = lang === 'en' ? 'System error' : '系统错误';
        notice(tips, result.message || msg, 'error');
        return Promise.reject(result);
      case 402: // 未支付
        return Promise.reject(result);
      default:
        return result;
    }
  },
  error => {
    // console.log(error)
    // 网络错误处理
    const lang = getLanguage();
    const tips = lang === 'en' ? 'error' : '错误';
    const msg = lang === 'en' ? 'Request canceled' : '请求已取消';
    const msg1 = lang === 'en' ? 'Network request failed' : '网络请求失败';
    const errMsg = axios.isCancel(error) 
      ? (error.response?.data?.message || msg) 
      : msg1;
    if(error.code !== 'ERR_CANCELED'){
      notice(tips, errMsg, 'error');
    }    
    return Promise.reject(error);
  }
);

// 处理未授权状态(401)
const handleUnauthorized = async (result) => {
  const lang = getLanguage();
  const msg = lang === 'en' ? 'Login status has expired' : '登录状态已过期';
  const cancelReason = result.message || msg;
  
  // 1. 取消所有待处理请求
  const cancelMessages = [];
  pendingRequests.forEach((controller, key) => {
    controller.abort(cancelReason);
    cancelMessages.push(key);
    pendingRequests.delete(key);
  });

  const store = require("@/store").default;
  // 2. 【新增】清除所有支付定时器
  await store.dispatch('stopAllPaymentPolling');

  // 3. 清理用户状态
  await clearLogin();
  
  // 4. 路由跳转
  if (router.currentRoute._value.name !== 'HomeView') {
    router.replace({ name: 'UserLogin' });
  }
  
  // 5. 错误通知，注意：由于登录失效会取消其它数据请求，此时页面立即跳转，所以通知需要延迟显示，否则提示会一闪而过
  setTimeout(() => {
    const lang = getLanguage();
    const tips = lang === 'en' ? 'error' : '错误';
    notice(tips, cancelReason, 'error');
  }, 1);
};

// 导出HTTP实例
export const http = customAxios;

// 手动取消所有请求的API方法
export const cancelAllRequests = () => {
  const lang = getLanguage();
  const cancelReason = lang === 'en' ? 'User voluntarily cancels' : '用户主动取消';
  pendingRequests.forEach(controller => controller.abort(cancelReason));
  pendingRequests.clear();
};